if (!customElements.get("ugc-block")) {
  class UGCBlock extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.inView = false;
      this.isMuted = true;
      this.isPaused = false;
      this.wheelLocked = false;
      this.raf = null;
      this.pointer = null;
      this.releaseTimer = null;
      this.onResize = null;
      this.slidePositions = [];
    }

    connectedCallback() {
      this.viewport = this.querySelector("[data-ugc-viewport]");
      this.track = this.querySelector("[data-ugc-track]");
      this.slides = Array.from(this.querySelectorAll("[data-ugc-slide]"));
      this.prevButton = this.querySelector("[data-ugc-prev]");
      this.nextButton = this.querySelector("[data-ugc-next]");
      this.videos = this.slides.map((slide) => slide.querySelector("[data-ugc-video]"));

      if (!this.viewport || !this.track || !this.slides.length) return;

      const initialIndex = Number(this.dataset.initialIndex || 0);
      this.activeIndex = this.clampIndex(initialIndex);

      this.bindEvents();
      this.observe();
      this.scheduleLayout();
      this.setActive(this.activeIndex, false);
    }

    disconnectedCallback() {
      this.pauseAll();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.releaseTimer) window.clearTimeout(this.releaseTimer);
      if (this.onResize) window.removeEventListener("resize", this.onResize);
    }

    bindEvents() {
      this.prevButton && this.prevButton.addEventListener("click", () => this.goTo(this.activeIndex - 1));
      this.nextButton && this.nextButton.addEventListener("click", () => this.goTo(this.activeIndex + 1));

      this.viewport.addEventListener("wheel", (event) => this.onWheel(event), { passive: false });
      this.viewport.addEventListener("pointerdown", (event) => this.onPointerDown(event));
      this.viewport.addEventListener("pointerup", (event) => this.onPointerUp(event));
      this.viewport.addEventListener("pointercancel", () => (this.pointer = null));

      this.querySelectorAll("[data-ugc-products-toggle]").forEach((toggle) => {
        toggle.addEventListener("click", () => this.toggleProducts(toggle));
      });

      this.querySelectorAll("[data-ugc-mute]").forEach((button) => {
        button.addEventListener("click", () => this.toggleMute(button));
      });

      this.querySelectorAll("[data-ugc-play]").forEach((button) => {
        button.addEventListener("click", () => this.togglePlay(button));
      });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener("shopify:block:select", (event) => this.onBlockSelect(event));
      }
    }

    observe() {
      if (!("IntersectionObserver" in window)) {
        this.inView = true;
        this.playActive();
      } else {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.inView = Boolean(entry && entry.isIntersecting && entry.intersectionRatio > 0.35);
            if (this.inView) {
              this.playActive();
            } else {
              this.pauseAll();
            }
          },
          { threshold: [0, 0.35, 0.7] }
        );
        this.intersectionObserver.observe(this);
      }

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.resizeObserver.observe(this.viewport);
      } else {
        this.onResize = () => this.scheduleLayout();
        window.addEventListener("resize", this.onResize);
      }
    }

    onWheel(event) {
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (Math.abs(delta) < 18 || this.wheelLocked) return;

      const direction = delta > 0 ? 1 : -1;
      const nextIndex = this.wrapIndex(this.activeIndex + direction);
      if (nextIndex === this.activeIndex) return;

      event.preventDefault();
      this.wheelLocked = true;
      this.goTo(nextIndex);
      window.setTimeout(() => {
        this.wheelLocked = false;
      }, 520);
    }

    onPointerDown(event) {
      if (event.pointerType === "mouse") return;
      this.pointer = { x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.pointer) return;
      const diffX = event.clientX - this.pointer.x;
      const diffY = event.clientY - this.pointer.y;
      this.pointer = null;
      if (Math.abs(diffX) < 38 || Math.abs(diffX) < Math.abs(diffY)) return;
      this.goTo(this.activeIndex + (diffX < 0 ? 1 : -1));
    }

    onBlockSelect(event) {
      const slide = event.target.closest("[data-ugc-slide]");
      if (!slide || !this.contains(slide)) return;
      const index = this.slides.indexOf(slide);
      if (index >= 0) this.goTo(index);
    }

    toggleProducts(toggle) {
      const panel = toggle.closest("[data-ugc-products]");
      if (!panel) return;
      const shouldOpen = !panel.classList.contains("is-open");
      this.closeProducts(panel);
      const isOpen = panel.classList.toggle("is-open", shouldOpen);
      toggle.setAttribute("aria-expanded", String(isOpen));
    }

    closeProducts(exceptPanel) {
      this.querySelectorAll("[data-ugc-products].is-open").forEach((panel) => {
        if (panel === exceptPanel) return;
        panel.classList.remove("is-open");
        const toggle = panel.querySelector("[data-ugc-products-toggle]");
        if (toggle) toggle.setAttribute("aria-expanded", "false");
      });
    }

    toggleMute(button) {
      this.isMuted = !this.isMuted;
      this.querySelectorAll("[data-ugc-mute]").forEach((muteButton) => {
        muteButton.setAttribute("aria-pressed", String(!this.isMuted));
      });
      const activeVideo = this.videos[this.activeIndex];
      if (activeVideo) {
        activeVideo.muted = this.isMuted;
        this.playVideo(activeVideo);
      }
    }

    togglePlay(button) {
      const activeVideo = this.videos[this.activeIndex];
      if (!activeVideo) return;

      this.isPaused = !this.isPaused;
      this.updatePlayControls();

      if (this.isPaused) {
        activeVideo.pause();
        return;
      }

      this.playActive();
      if (button) button.blur();
    }

    goTo(index) {
      const nextIndex = this.wrapIndex(index);
      if (nextIndex === this.activeIndex) return;
      this.setActive(nextIndex, true);
    }

    setActive(index, shouldPlay) {
      const nextIndex = this.wrapIndex(index);
      const changed = nextIndex !== this.activeIndex;
      this.closeProducts();
      if (changed) this.isPaused = false;
      this.activeIndex = nextIndex;
      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-active", slideIndex === this.activeIndex);
      });

      this.positionSlides();
      this.updateControls();
      this.updatePlayControls();
      this.scheduleLayout();
      this.pauseInactive();
      this.releaseDistantVideos();

      if (shouldPlay || this.inView) this.playActive();
    }

    scheduleLayout() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.layout());
    }

    layout() {
      this.raf = null;
      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;

      const styles = window.getComputedStyle(this.track);
      const viewportStyles = window.getComputedStyle(this.viewport);
      const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
      const paddingLeft = parseFloat(viewportStyles.paddingLeft || "0") || 0;
      const paddingRight = parseFloat(viewportStyles.paddingRight || "0") || 0;
      const slideWidth = activeSlide.offsetWidth;
      const viewportWidth = this.viewport.clientWidth - paddingLeft - paddingRight;
      const activePosition = this.slidePositions.filter((position) => position < 0).length;
      const offset = viewportWidth / 2 - slideWidth / 2 - activePosition * (slideWidth + gap);
      this.track.style.transform = `translate3d(${Math.round(offset)}px, 0, 0)`;
    }

    positionSlides() {
      this.slidePositions = this.slides.map((slide, index) => {
        const position = this.getCircularPosition(index);
        slide.style.order = String(position);
        return position;
      });
    }

    updateControls() {
      if (this.prevButton) this.prevButton.disabled = this.slides.length < 2;
      if (this.nextButton) this.nextButton.disabled = this.slides.length < 2;
    }

    updatePlayControls() {
      this.querySelectorAll("[data-ugc-play]").forEach((button) => {
        button.setAttribute("aria-pressed", String(this.isPaused));
        button.setAttribute("aria-label", this.isPaused ? "Play video" : "Pause video");
      });
    }

    pauseInactive() {
      this.videos.forEach((video, index) => {
        if (!video || index === this.activeIndex) return;
        video.pause();
      });
    }

    pauseAll() {
      this.videos.forEach((video) => video && video.pause());
    }

    playActive() {
      const video = this.videos[this.activeIndex];
      if (!video || !this.inView || this.isPaused) return;
      this.hydrateVideo(video);
      video.muted = this.isMuted;
      this.playVideo(video);
    }

    playVideo(video) {
      const media = video.closest(".m-ugc-card__media");
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise
          .then(() => media && media.classList.add("is-ready"))
          .catch(() => {
            video.muted = true;
            this.isMuted = true;
            this.querySelectorAll("[data-ugc-mute]").forEach((button) => {
              button.setAttribute("aria-pressed", "false");
            });
            video.play().then(() => media && media.classList.add("is-ready")).catch(() => {});
          });
      } else if (media) {
        media.classList.add("is-ready");
      }
    }

    hydrateVideo(video) {
      if (video.dataset.hydrated === "true") return;
      const source = video.querySelector("source[data-src]");
      if (!source) return;
      source.src = source.dataset.src;
      video.load();
      video.dataset.hydrated = "true";
    }

    releaseDistantVideos() {
      window.clearTimeout(this.releaseTimer);
      this.releaseTimer = window.setTimeout(() => {
        this.videos.forEach((video, index) => {
          if (!video || Math.abs(this.getCircularPosition(index)) <= 1 || video.dataset.hydrated !== "true") return;
          const source = video.querySelector("source");
          if (source) source.removeAttribute("src");
          video.pause();
          video.removeAttribute("src");
          video.load();
          video.dataset.hydrated = "false";
          const media = video.closest(".m-ugc-card__media");
          media && media.classList.remove("is-ready");
        });
      }, 1200);
    }

    clampIndex(index) {
      return Math.max(0, Math.min(this.slides.length - 1, index));
    }

    wrapIndex(index) {
      if (!this.slides.length) return 0;
      return ((index % this.slides.length) + this.slides.length) % this.slides.length;
    }

    getCircularPosition(index) {
      const count = this.slides.length;
      if (count < 2) return 0;

      let position = index - this.activeIndex;
      if (position > count / 2) position -= count;
      if (position < count / -2) position += count;

      return position;
    }
  }

  customElements.define("ugc-block", UGCBlock);
}
